import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import ContractCharts from "./ContractCharts";
import {
  CONTRACT_PRIORITIES,
  CONTRACT_STATUS_OPTIONS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  normalizeContractResponse,
} from "./constants";

const EMPTY_FILTERS = {
  title: "",
  priority: [],
  status: [],
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : "--";
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "--";
};

export default function ContractList({
  facilityIds = [],
  facilities = [],
  onFacilityChange,
  loadRequests,
  getDetailPath,
  showFacilityColumn = false,
  showRequesterColumn = false,
  emptyText,
}) {
  const navigate = useNavigate();
  const actionRef = useRef();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loadedRows, setLoadedRows] = useState([]);

  const request = useCallback(async (params) => {
    if (!facilityIds.length) {
      setLoadedRows([]);
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(facilityIds, {
        page: params.current,
        page_size: params.pageSize,
        title: filters.title.trim(),
        priority: filters.priority,
        status: filters.status,
      });
      const { rows, total } = normalizeContractResponse(response);
      setLoadedRows(rows);
      return { data: rows, total, success: true };
    } catch (error) {
      setLoadedRows([]);
      message.error("Failed to load contracts");
      return { data: [], total: 0, success: false };
    }
  }, [facilityIds, filters, loadRequests]);

  // Facilities are loaded asynchronously for the contract portal. ProTable does
  // not automatically re-run its request callback when those props change, so
  // explicitly reload after the initial facility list or filters are updated.
  useEffect(() => {
    setLoadedRows([]);
    actionRef.current?.reload?.();
  }, [facilityIds, filters]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <a onClick={() => openDetail(record)}>{value}</a>
      ),
    },
    {
      title: "Work Order #",
      dataIndex: "workorder_number",
      key: "workorder_number",
      width: 140,
    },
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Assignee",
      dataIndex: "assignTo_name",
      key: "assignTo_name",
      width: 160,
    },
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Est. Budget",
      dataIndex: "estimate_budget",
      key: "estimate_budget",
      width: 115,
      render: formatMoney,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value) => (
        <Tag color={CONTRACT_STATUS_COLORS[value]}>
          {CONTRACT_STATUS_LABELS[value] || value}
        </Tag>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 120,
      render: formatDate,
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 150,
      render: (value) => {
        if (!value) return "--";
        const date = moment(value);
        return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "--";
      },
    },
  ], [openDetail, showFacilityColumn, showRequesterColumn]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    if (value === null || value === undefined) return;

    setFilters((current) => {
      const values = current[key] || [];
      const exists = values.some((item) => String(item) === String(value));
      return {
        ...current,
        [key]: exists
          ? values.filter((item) => String(item) !== String(value))
          : [...values, value],
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onFacilityChange?.([]);
  }, [onFacilityChange]);

  const renderSearches = useCallback(() => (
    <Form className="filter-form contract-filter-form" autoComplete="off">
      {facilities.length > 0 ? (
        <Form.Item label="Facility" name="facility">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Facilities"
            placeholder="Facility"
            value={facilityIds}
            options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onFacilityChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Title" name="title">
        <Input
          allowClear
          placeholder="Search title"
          value={filters.title}
          onChange={(event) => updateFilter("title", event.target.value)}
        />
      </Form.Item>
      <Form.Item label="Priority" name="priority">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Priorities"
          placeholder="Priority"
          value={filters.priority}
          options={CONTRACT_PRIORITIES.map((value) => ({ value, label: value }))}
          onChange={(value) => updateFilter("priority", value || [])}
        />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Statuses"
          placeholder="Status"
          value={filters.status}
          options={CONTRACT_STATUS_OPTIONS}
          onChange={(value) => updateFilter("status", value || [])}
        />
      </Form.Item>
    </Form>
  ), [facilities, facilityIds, filters, onFacilityChange, updateFilter]);

  const filterHasValue = Boolean(
    facilityIds.length ||
    filters.title.trim() ||
    filters.priority.length ||
    filters.status.length
  );

  const pageHeader = (
    <PageHeader
      hide_imports
      hide_search
      hid_new
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={renderSearches}
      resetFilters={resetFilters}
      filterHasValue={filterHasValue}
    />
  );

  return (
    <div style={{ width: "100%", minWidth: 0, padding: 16 }}>
      <ContractCharts
        rows={loadedRows}
        filters={filters}
        onPrioritySelect={(value) => toggleFilterValue("priority", value)}
        onStatusSelect={(value) => toggleFilterValue("status", value)}
      />
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={false}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: showFacilityColumn || showRequesterColumn ? 1480 : 1280 }}
        toolBarRender={() => pageHeader}
        locale={{ emptyText: emptyText || (facilityIds.length ? "No contracts" : "Select one or more facilities") }}
      />
    </div>
  );
}
